import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import { createRouteBufferPolygon } from '../utils/routeBufferPolygon'
import type { GeoJSON } from 'geojson'

export interface UseRouteBufferLayerProps {
  routeGeometry?: GeoJSON.LineString | GeoJSON.MultiLineString | null
  radiusMiles?: number | null
}

/**
 * Hook for displaying a route buffer polygon on the map
 * Shows the search radius area visually
 */
export function useRouteBufferLayer({
  routeGeometry,
  radiusMiles
}: UseRouteBufferLayerProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const previousGeometryRef = useRef<string | null>(null)
  const previousRadiusRef = useRef<number | null>(null)

  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) return

    // Check if geometry or radius changed
    const geometryKey = routeGeometry ? JSON.stringify(routeGeometry) : null
    const geometryChanged = previousGeometryRef.current !== geometryKey
    const radiusChanged = previousRadiusRef.current !== radiusMiles

    if (!geometryChanged && !radiusChanged) {
      return
    }

    previousGeometryRef.current = geometryKey
    previousRadiusRef.current = radiusMiles ?? null

    // Remove existing buffer layer if it exists
    if (map.current.getLayer('route-buffer')) {
      map.current.removeLayer('route-buffer')
    }
    if (map.current.getSource('route-buffer')) {
      map.current.removeSource('route-buffer')
    }

    // If no geometry or radius, don't show buffer
    if (!routeGeometry || radiusMiles === null || radiusMiles === undefined) {
      return
    }

    try {
      // Create buffer polygon
      const bufferPolygon = createRouteBufferPolygon(routeGeometry, radiusMiles)
      
      if (!bufferPolygon) {
        return
      }

      const geoJsonData: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {
              radiusMiles
            },
            geometry: bufferPolygon
          }
        ]
      }

      // Add source
      map.current.addSource('route-buffer', {
        type: 'geojson',
        data: geoJsonData
      })

      // Add fill layer
      map.current.addLayer({
        id: 'route-buffer',
        type: 'fill',
        source: 'route-buffer',
        paint: {
          'fill-color': '#5C8DC5', // Primary brand color
          'fill-opacity': 0.15 // Semi-transparent
        }
      })

      // Add outline layer for better visibility
      map.current.addLayer({
        id: 'route-buffer-outline',
        type: 'line',
        source: 'route-buffer',
        paint: {
          'line-color': '#5C8DC5',
          'line-width': 2,
          'line-opacity': 0.4,
          'line-dasharray': [2, 2]
        }
      })

      // Ensure buffer is below routes and markers but above base map
      const layersToMoveAbove = [
        'route-lines',
        'dead-end-markers',
        'waypoint-markers',
        'poi-markers',
        'forest-roads'
      ]
      
      for (const layerId of layersToMoveAbove) {
        if (map.current.getLayer(layerId)) {
          try {
            map.current.moveLayer('route-buffer-outline', layerId)
            map.current.moveLayer('route-buffer', 'route-buffer-outline')
            break
          } catch (e) {
            // Layer might not exist, continue
          }
        }
      }
    } catch (error) {
    }
  }, [map, isLoaded, styleLoaded, routeGeometry, radiusMiles])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (map.current) {
        try {
          if (map.current.getLayer('route-buffer-outline')) {
            map.current.removeLayer('route-buffer-outline')
          }
          if (map.current.getLayer('route-buffer')) {
            map.current.removeLayer('route-buffer')
          }
          if (map.current.getSource('route-buffer')) {
            map.current.removeSource('route-buffer')
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    }
  }, [map])
}

